/**
 * HazardSystem - Handles lava, spikes, and other deadly hazards
 * 
 * Consolidates all instant-death hazards into one system.
 * 
 * @module systems/HazardSystem
 */

import Phaser from 'phaser';
import BaseSystem from './BaseSystem.js';

export default class HazardSystem extends BaseSystem {
    constructor(scene) {
        super(scene);
        this.lavaZones = [];
        this.spikes = [];
    }

    /**
     * Create hazards from level config
     * @param {Object} config - Level configuration
     */
    create(config) {
        this.createLavaZones(config);
        this.createSpikes(config);
        this.isActive = this.lavaZones.length > 0 || this.spikes.length > 0;
    }

    /**
     * Create lava zones
     * @param {Object} config - Level configuration
     */
    createLavaZones(config) {
        if (!config.lavaZones) return;

        config.lavaZones.forEach(l => {
            const lavaBody = this.scene.matter.add.rectangle(
                l.x + l.w / 2,
                l.y + l.h / 2,
                l.w,
                l.h,
                { isStatic: true, isSensor: true, label: 'lava' }
            );

            // Lava visual
            const lavaGraphics = this.scene.add.graphics();
            lavaGraphics.fillStyle(0xff4500, 1);
            lavaGraphics.fillRect(l.x, l.y, l.w, l.h);

            // Surface glow
            lavaGraphics.fillStyle(0xffff00, 0.3);
            lavaGraphics.fillRect(l.x, l.y, l.w, 10);

            // Animated bubbles
            const bubbles = [];
            for (let i = 0; i < l.w / 50; i++) {
                const bubble = this.createLavaBubble(
                    l.x + Phaser.Math.Between(20, l.w - 20), 
                    l.y
                );
                bubbles.push(bubble);
            }

            this.lavaZones.push({ 
                body: lavaBody, 
                graphics: lavaGraphics,
                bubbles 
            });
            this.items.push({ body: lavaBody, visual: lavaGraphics });
        });
    }

    /**
     * Create animated lava bubble
     * @param {number} x - X position
     * @param {number} baseY - Base Y position
     * @returns {Phaser.GameObjects.Arc}
     */
    createLavaBubble(x, baseY) {
        const bubble = this.scene.add.circle(
            x, 
            baseY, 
            Phaser.Math.Between(3, 8), 
            0xffff00, 
            0.6
        );
        
        this.scene.tweens.add({
            targets: bubble,
            y: baseY - 30,
            alpha: 0,
            scale: 1.5,
            duration: Phaser.Math.Between(1000, 2000),
            delay: Phaser.Math.Between(0, 2000),
            repeat: -1,
            onRepeat: () => {
                bubble.x = x + Phaser.Math.Between(-10, 10);
                bubble.y = baseY;
            }
        });

        return bubble;
    }

    /**
     * Create spikes
     * @param {Object} config - Level configuration
     */
    createSpikes(config) {
        if (!config.spikes) return;

        config.spikes.forEach(s => {
            const spikeHeight = 25;
            const spikeWidth = 20;
            const centerY = s.y - spikeHeight / 2;

            const body = this.scene.matter.add.polygon(s.x, centerY, 3, spikeHeight, {
                isStatic: true,
                label: 'spike',
                isSensor: true,
                angle: 0
            });

            const visual = this.scene.add.graphics();

            // Main spike
            visual.fillStyle(0x505050);
            visual.beginPath();
            visual.moveTo(s.x, s.y - spikeHeight);
            visual.lineTo(s.x - spikeWidth / 2, s.y);
            visual.lineTo(s.x + spikeWidth / 2, s.y);
            visual.closePath();
            visual.fill();

            // Highlight
            visual.lineStyle(2, 0x707070);
            visual.beginPath();
            visual.moveTo(s.x, s.y - spikeHeight);
            visual.lineTo(s.x + spikeWidth / 2, s.y);
            visual.stroke();

            // Base
            visual.fillStyle(0x404040);
            visual.fillRect(s.x - spikeWidth / 2 - 3, s.y - 3, spikeWidth + 6, 6);

            this.spikes.push({ body, visual });
            this.items.push({ body, visual });
        });
    }

    /**
     * Check if a body belongs to this system
     * @param {Object} body - Physics body
     * @returns {boolean}
     */
    ownsBody(body) {
        return body.label === 'lava' || body.label === 'spike';
    }

    /**
     * Handle collision - always kills player
     * @param {Object} body - Hazard body
     * @param {Object} player - Player reference
     * @returns {boolean} True if this was a hazard collision
     */
    handleCollision(body, player) {
        if (!this.ownsBody(body)) return false;
        
        // Signal that player should die
        return true;
    }

    /**
     * Cleanup
     */
    destroy() {
        this.lavaZones.forEach(zone => {
            if (zone.bubbles) {
                zone.bubbles.forEach(b => b.destroy());
            }
        });
        this.lavaZones = [];
        this.spikes = [];
        super.destroy();
    }
}
